import React, { useState, useEffect } from 'react';
import './ProductDetail.css';
import { useCart } from '../../context/CartContext';
import { useFavourites } from '../../context/FavouritesContext';

export default function ProductDetail({ product, onClose }) {
  const [activeImg, setActiveImg] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [added, setAdded] = useState(false);
  const [sizeError, setSizeError] = useState(false);

  // New Context Functionality
  const { addToCart } = useCart();
  const { isFavourited, toggleFavourite } = useFavourites();

  // Close on Escape key
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleAddToCart = () => {
    if (!selectedSize) {
      setSizeError(true);
      setTimeout(() => setSizeError(false), 1800);
      return;
    }
    // Updated with Context call
    addToCart(product, selectedSize);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  const discount = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;

  return (
    <div className="pd-backdrop" onClick={onClose}>
      <div className="pd" onClick={e => e.stopPropagation()}>

        {/* Close button */}
        <button className="pd__close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* ── LEFT: image gallery ── */}
        <div className="pd__gallery">
          {/* Thumbnail strip */}
          <div className="pd__thumbs">
            {product.images.map((img, i) => (
              <button
                key={i}
                className={`pd__thumb${i === activeImg ? ' pd__thumb--on' : ''}`}
                onClick={() => setActiveImg(i)}
              >
                <img src={img} alt={`View ${i + 1}`} />
              </button>
            ))}
          </div>

          {/* Main image */}
          <div className="pd__main-img-wrap">
            <img
              key={activeImg}
              src={product.images[activeImg]}
              alt={product.name}
              className="pd__main-img"
            />
            {/* Discount badge on image */}
            {discount && (
              <span className="pd__discount-badge">-{discount}%</span>
            )}
          </div>
        </div>

        {/* ── RIGHT: product info ── */}
        <div className="pd__info">
          {/* Brand + tag */}
          <div className="pd__info-top">
            <span className="pd__brand">{product.brand}</span>
            {product.tag && (
              <span className={`pd__tag pd__tag--${product.tag.toLowerCase()}`}>
                {product.tag}
              </span>
            )}
          </div>

          {/* Name */}
          <h2 className="pd__name">{product.name}</h2>
          <p className="pd__colorway">{product.colorway}</p>

          {/* Price */}
          <div className="pd__price-row">
            <span className="pd__price">R {product.price.toLocaleString()}</span>
            {product.originalPrice && (
              <span className="pd__original">R {product.originalPrice.toLocaleString()}</span>
            )}
          </div>

          {/* Description */}
          <p className="pd__desc">{product.description}</p>

          {/* Size selector */}
          <div className="pd__sizes-section">
            <div className="pd__sizes-header">
              <p className="pd__sizes-label">Select Size (UK)</p>
              {selectedSize && (
                <p className="pd__sizes-chosen">UK {selectedSize}</p>
              )}
            </div>
            <div className={`pd__sizes${sizeError ? ' pd__sizes--error' : ''}`}>
              {product.sizes.map(size => (
                <button
                  key={size}
                  className={`pd__size${selectedSize === size ? ' pd__size--on' : ''}`}
                  onClick={() => { setSelectedSize(size); setSizeError(false); }}
                >
                  {size}
                </button>
              ))}
            </div>
            {sizeError && (
              <p className="pd__size-err">Please select a size first</p>
            )}
          </div>

          {/* Details list */}
          <ul className="pd__details">
            {product.details.map(d => (
              <li key={d} className="pd__detail-item">
                <span className="pd__detail-dot" />
                {d}
              </li>
            ))}
          </ul>

          {/* Action buttons */}
          <div className="pd__actions">
            <button
              className={`pd__add-btn${added ? ' pd__add-btn--done' : ''}${!product.inStock ? ' pd__add-btn--oos' : ''}`}
              onClick={handleAddToCart}
              disabled={!product.inStock}
            >
              {!product.inStock ? (
                'OUT OF STOCK'
              ) : added ? (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  ADDED TO CART
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 2 3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <path d="M16 10a4 4 0 01-8 0"/>
                  </svg>
                  ADD TO CART
                </>
              )}
            </button>

            {/* Updated Wishlist Button */}
            <button 
              className="pd__wishlist-btn" 
              aria-label="Add to wishlist"
              onClick={() => toggleFavourite(product)}
              style={{ color: isFavourited(product.id) ? '#da291c' : undefined }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" 
                fill={isFavourited(product.id) ? 'currentColor' : 'none'}
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
              </svg>
            </button>
          </div>         
          </div>
        </div>
      </div>
  );
}